import * as React from 'react';

import styled from 'styled-components';

import { COLOR, FONT_SIZE, SPACING } from '../../packages/constants';
import { HelperModal, QuestionMark } from '../helper-modal';
import { NumberInput, NumberInputProps } from '../number-input';

export interface NumberFieldInlineProps extends Omit<NumberInputProps, 'ariaLabel'> {
    hasHelper?: boolean;
    helperContent?: React.ReactNode;
    label: string;
    validationMessage?: string;
    thinLabel?: boolean;
    labelWidth?: string;
    labelAlignment?: 'start' | 'center' | 'end' | undefined;
    symbol?: string;
    symbolPlacement?: 'before' | 'after' | undefined;
}

export interface NumberFieldProps extends Omit<NumberInputProps, 'ariaLabel'> {
    hasHelper?: boolean;
    helperContent?: React.ReactNode;
    label: string;
    validationMessage?: string;
    thinLabel?: boolean;
}

interface StyledTextLabelProps {
    disabled?: boolean;
    invalid?: boolean;
    thinLabel?: boolean;
}

interface WrapperProps {
    labelWidth?: string;
    symbolPlacement?: 'before' | 'after' | undefined;
}

interface StyledLabelWrapperProps {
    labelAlignment: 'start' | 'center' | 'end';
}

interface StyledValidationMessageProps {
    invalid?: boolean;
    symbolPlacement?: 'before' | 'after' | undefined;
}

interface StyledSymbolProps {
    invalid?: boolean;
    symbolPlacement: 'before' | 'after';
}

const Wrapper = styled.div<WrapperProps>`
    display: grid;
    grid-template-columns:
        ${(props: WrapperProps): string => (props.labelWidth ? props.labelWidth : 'auto')}
        ${(props: WrapperProps): string => (!props.symbolPlacement ? 'minmax(0, 1fr)' : '')}
        ${(props: WrapperProps): string => (props.symbolPlacement === 'before' ? 'auto minmax(0, 1fr)' : '')}
        ${(props: WrapperProps): string => (props.symbolPlacement === 'after' ? 'minmax(0, 1fr) auto' : '')};
`;

const StyledLabelWrapper = styled.div<StyledLabelWrapperProps>`
    display: flex;
    justify-content: center;
    align-items: ${(props: StyledLabelWrapperProps): string => props.labelAlignment};
    flex-direction: column;
    padding-right: ${SPACING.LARGE};
`;

const StyledTextLabel = styled.label<StyledTextLabelProps>`
    margin-right: ${SPACING.X_SMALL};
    font-size: ${FONT_SIZE.SMALL};
    line-height: ${FONT_SIZE.LARGE};
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: ${(props: StyledTextLabelProps): string => (props.thinLabel ? '400' : '700')};
    color: ${(props: StyledTextLabelProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.LABEL_TEXT)};
    opacity: ${(props: StyledTextLabelProps): string => (props.disabled ? '0.7' : '1')};
`;

const StyledLabelHasHelper = styled.div`
    display: inline-flex;
    max-width: 100%;
`;

const StyledValidationMessage = styled.div<StyledValidationMessageProps>`
    font-size: ${FONT_SIZE.X_SMALL};
    line-height: ${FONT_SIZE.SMALL};
    color: ${(props: StyledValidationMessageProps): string =>
        props.invalid ? COLOR.PRIMARY.RED : COLOR.PRIMARY.BLACK};
    padding-top: ${SPACING.SMALL};
    overflow: hidden;
    text-overflow: ellipsis;
    grid-area: ${(props: StyledValidationMessageProps): string => (!props.symbolPlacement ? '2/2' : '')}
        ${(props: StyledValidationMessageProps): string => (props.symbolPlacement === 'before' ? '2/3' : '')}
        ${(props: StyledValidationMessageProps): string => (props.symbolPlacement === 'after' ? '2/2' : '')};
`;

const StyledSymbol = styled.span<StyledSymbolProps>`
    font-size: ${FONT_SIZE.SMALL};
    color: ${(props: StyledSymbolProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.PRIMARY.BLACK)};
    align-self: center;
    margin: ${(props: StyledSymbolProps): string =>
        props.symbolPlacement === 'before' ? `0 ${SPACING.SMALL} 0 0` : `0 0 0 ${SPACING.SMALL}`};
`;
export const NumberFieldInline = React.forwardRef<HTMLInputElement, NumberFieldInlineProps>(
    (
        {
            autoComplete,
            autoFocus,
            dataId,
            defaultValue,
            disabled,
            hasHelper,
            helperContent,
            id,
            invalid,
            label,
            max,
            min,
            name,
            placeholder,
            required = false,
            step,
            onChange,
            thinLabel,
            labelWidth,
            labelAlignment = 'start',
            value,
            className,
            validationMessage,
            symbol,
            symbolPlacement,
            ...props
        },
        ref,
    ) => {
        return (
            <Wrapper
                className={className}
                labelWidth={labelWidth}
                symbolPlacement={symbol ? symbolPlacement : undefined}
                {...props}
            >
                <StyledLabelWrapper labelAlignment={labelAlignment}>
                    {hasHelper ? (
                        <StyledLabelHasHelper>
                            <StyledTextLabel invalid={invalid} disabled={disabled} thinLabel={thinLabel} htmlFor={id}>
                                {label}
                            </StyledTextLabel>
                            <HelperModal dataId={dataId} modalContent={helperContent}>
                                <QuestionMark />
                            </HelperModal>
                        </StyledLabelHasHelper>
                    ) : (
                        <StyledTextLabel
                            data-test-id={`${dataId}-label`}
                            invalid={invalid}
                            disabled={disabled}
                            thinLabel={thinLabel}
                            htmlFor={id}
                        >
                            {label}
                        </StyledTextLabel>
                    )}
                </StyledLabelWrapper>
                {symbol && symbolPlacement === 'before' && (
                    <StyledSymbol invalid={invalid} symbolPlacement={symbolPlacement}>
                        {symbol}
                    </StyledSymbol>
                )}
                <NumberInput
                    autoComplete={autoComplete}
                    autoFocus={autoFocus}
                    dataId={`${dataId}-input`}
                    defaultValue={defaultValue}
                    disabled={disabled}
                    id={id}
                    invalid={invalid}
                    max={max}
                    min={min}
                    name={name}
                    onChange={onChange}
                    placeholder={placeholder}
                    ref={ref}
                    required={required}
                    step={step}
                    value={value}
                />
                {symbol && symbolPlacement === 'after' && (
                    <StyledSymbol invalid={invalid} symbolPlacement={symbolPlacement}>
                        {symbol}
                    </StyledSymbol>
                )}
                {validationMessage && (
                    <StyledValidationMessage invalid={invalid} symbolPlacement={symbol ? symbolPlacement : undefined}>
                        {validationMessage}
                    </StyledValidationMessage>
                )}
            </Wrapper>
        );
    },
);

NumberFieldInline.displayName = 'NumberFieldInline';
